<template>
    <div
        v-if="showLauncher"
        :class="inlineLauncher ? 'relative z-10' : 'fixed right-4 top-4 z-[60] lg:right-6 lg:top-6'"
    >
        <button
            type="button"
            class="group inline-flex items-center justify-center text-sm font-semibold transition"
            :class="inlineLauncher
                ? 'h-10 w-10 rounded-lg border border-slate-200 bg-white text-slate-700 shadow-sm hover:border-indigo-200 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20'
                : 'h-11 gap-2 rounded-full border border-white/70 bg-white/95 px-3 text-slate-800 shadow-lg shadow-slate-900/10 backdrop-blur hover:border-indigo-200 hover:text-indigo-700'"
            :title="t('tamepulse.requestCenter.open')"
            :aria-label="t('tamepulse.requestCenter.open')"
            @click="drawerOpen = true"
        >
            <span
                class="relative inline-flex items-center justify-center transition"
                :class="inlineLauncher
                    ? 'h-5 w-5 text-slate-700 group-hover:text-indigo-700'
                    : 'h-8 w-8 rounded-full bg-indigo-50 text-indigo-700 group-hover:bg-indigo-100'"
            >
                <fa-icon icon="flag" class="size-4" />
                <span
                    v-if="pendingCount > 0"
                    class="absolute inline-flex min-w-5 items-center justify-center rounded-full bg-rose-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white ring-2 ring-white"
                    :class="inlineLauncher ? '-right-3 -top-3' : '-right-1 -top-1'"
                >
                    {{ displayPendingCount }}
                </span>
            </span>
            <span v-if="!inlineLauncher" class="hidden sm:inline">{{ t('tamepulse.requestCenter.launcher') }}</span>
        </button>
    </div>

    <SideDrawer
        v-model="drawerOpen"
        :title="t('tamepulse.requestCenter.title')"
        :close-label="t('tamepulse.requestCenter.close')"
    >
        <template #subtitle>
            {{ t('tamepulse.requestCenter.subtitle', { count: pendingCount }) }}
        </template>

        <div class="space-y-5">
            <div v-if="centerLoading" class="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                <fa-icon icon="circle-notch" class="mr-2 animate-spin" />
                {{ t('tamepulse.requestCenter.loading') }}
            </div>

            <div
                v-else-if="!hasPendingItems"
                class="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-5 text-sm text-slate-600"
            >
                <div class="font-semibold text-slate-900">{{ t('tamepulse.requestCenter.emptyTitle') }}</div>
                <div class="mt-1">{{ t('tamepulse.requestCenter.emptyDescription') }}</div>
            </div>

            <template v-else>
                <section
                    v-for="group in groupedNotices"
                    :key="`notice-group-${group.key}`"
                    class="space-y-3"
                >
                    <div>
                        <div class="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                            {{ group.label }}
                        </div>
                        <div class="text-base font-semibold text-slate-950">
                            {{ group.name }}
                        </div>
                    </div>

                    <article
                        v-for="notice in group.notices"
                        :key="notice.id"
                        class="rounded-xl border border-amber-100 bg-amber-50/40 p-4 shadow-sm"
                    >
                        <div class="flex flex-wrap items-start justify-between gap-3">
                            <button
                                type="button"
                                class="flex min-w-0 flex-1 items-start gap-3 rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:ring-offset-2"
                                :aria-expanded="isItemOpen('notice', notice)"
                                :aria-controls="isItemOpen('notice', notice) ? itemPanelId('notice', notice) : undefined"
                                @click="toggleItem('notice', notice)"
                            >
                                <div class="min-w-0 flex-1">
                                    <div class="text-xs font-semibold uppercase tracking-[0.16em] text-amber-600">
                                        {{ noticeTypeLabel(notice) }}
                                    </div>
                                    <div class="mt-1 font-semibold text-slate-950">
                                        {{ notice.title || notice.node_label || t('tamepulse.requestCenter.types.notice') }}
                                    </div>
                                    <div v-if="noticeSummary(notice)" class="mt-1 truncate text-sm text-slate-600">
                                        {{ noticeSummary(notice) }}
                                    </div>
                                    <div class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                                        <span v-if="notice.run_id">{{ t('tamepulse.requestCenter.runLabel', { id: notice.run_id }) }}</span>
                                        <span v-if="notice.run_id" aria-hidden="true">&middot;</span>
                                        <span>{{ notice.run?.environment?.name || t('tamepulse.approvals.notAvailable') }}</span>
                                        <span aria-hidden="true">&middot;</span>
                                        <span>{{ formatDateTime(notice.created_at) }}</span>
                                    </div>
                                </div>
                                <fa-icon :icon="isItemOpen('notice', notice) ? 'chevron-up' : 'chevron-down'" class="mt-1 shrink-0 text-slate-400" />
                            </button>

                            <SmartRouterButton
                                v-if="notice.playbook_id && notice.run_id"
                                class="small secondary"
                                :to="{ name: 'tamepulse.playbook.runs', params: { id: notice.playbook_id }, query: { runId: notice.run_id } }"
                            >
                                <TextWithIcon icon="diagram-project">{{ t('tamepulse.approvals.openRun') }}</TextWithIcon>
                            </SmartRouterButton>
                        </div>

                        <div v-if="isItemOpen('notice', notice)" :id="itemPanelId('notice', notice)" class="mt-4 border-t border-amber-100 pt-4">
                            <div class="rounded-lg border border-amber-100 bg-white p-3 text-sm leading-6 text-slate-700">
                                <div class="whitespace-pre-wrap">{{ noticeMessage(notice) }}</div>
                            </div>

                            <div v-if="humanReviewVisitorMessage(notice)" class="mt-3 rounded-lg border border-amber-100 bg-white p-3 text-sm leading-6 text-slate-700">
                                <div class="mb-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                                    {{ t('tamepulse.requestCenter.visitorMessage') }}
                                </div>
                                <div class="whitespace-pre-wrap">{{ humanReviewVisitorMessage(notice) }}</div>
                            </div>

                            <Button
                                type="button"
                                class="mt-4 w-full secondary"
                                :disabled="isMarkingNoticeRead(notice)"
                                @click="markNoticeRead(notice)"
                            >
                                <TextWithIcon :icon="isMarkingNoticeRead(notice) ? 'circle-notch' : 'check'">
                                    {{ isMarkingNoticeRead(notice) ? t('tamepulse.requestCenter.markingRead') : t('tamepulse.requestCenter.markRead') }}
                                </TextWithIcon>
                            </Button>
                        </div>
                    </article>
                </section>

                <section
                    v-for="group in groupedRequests"
                    :key="group.key"
                    class="space-y-3"
                >
                    <div>
                        <div class="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                            {{ t('tamepulse.requestCenter.playbookGroup') }}
                        </div>
                        <div class="text-base font-semibold text-slate-950">
                            {{ group.name }}
                        </div>
                    </div>

                    <article
                        v-for="request in group.requests"
                        :key="request.id"
                        class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                    >
                        <div class="flex flex-wrap items-start justify-between gap-3">
                            <button
                                type="button"
                                class="flex min-w-0 flex-1 items-start gap-3 rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:ring-offset-2"
                                :aria-expanded="isItemOpen('request', request)"
                                :aria-controls="isItemOpen('request', request) ? itemPanelId('request', request) : undefined"
                                @click="toggleItem('request', request)"
                            >
                                <div class="min-w-0 flex-1">
                                    <div class="text-xs font-semibold uppercase tracking-[0.16em] text-indigo-500">
                                        {{ requestTypeLabel(request) }}
                                    </div>
                                    <div class="mt-1 font-semibold text-slate-950">
                                        {{ request.node_label || requestTypeLabel(request) }}
                                    </div>
                                    <div v-if="requestSummary(request)" class="mt-1 truncate text-sm text-slate-600">
                                        {{ requestSummary(request) }}
                                    </div>
                                    <div class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                                        <span>{{ t('tamepulse.requestCenter.runLabel', { id: request.run_id }) }}</span>
                                        <span aria-hidden="true">&middot;</span>
                                        <span>{{ request.run?.environment?.name || t('tamepulse.approvals.notAvailable') }}</span>
                                        <span aria-hidden="true">&middot;</span>
                                        <span>{{ formatDateTime(request.requested_at) }}</span>
                                    </div>
                                </div>
                                <fa-icon :icon="isItemOpen('request', request) ? 'chevron-up' : 'chevron-down'" class="mt-1 shrink-0 text-slate-400" />
                            </button>

                            <SmartRouterButton
                                v-if="request.playbook_id && request.run_id"
                                class="small secondary"
                                :to="{ name: 'tamepulse.playbook.runs', params: { id: request.playbook_id }, query: { runId: request.run_id } }"
                            >
                                <TextWithIcon icon="diagram-project">{{ t('tamepulse.approvals.openRun') }}</TextWithIcon>
                            </SmartRouterButton>
                        </div>

                        <div v-if="isItemOpen('request', request)" :id="itemPanelId('request', request)" class="mt-4 border-t border-slate-100 pt-4">
                        <div v-if="request.message" class="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm leading-6 text-slate-700">
                            <div class="mb-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                                {{ t('tamepulse.approvals.message') }}
                            </div>
                            <div class="whitespace-pre-wrap">{{ request.message }}</div>
                        </div>

                        <div v-if="contextFields(request).length" class="mt-4 space-y-2">
                            <div class="text-sm font-semibold text-slate-900">{{ t('tamepulse.approvals.context') }}</div>
                            <div class="grid gap-2">
                                <div
                                    v-for="field in contextFields(request)"
                                    :key="`${request.id}-context-${field.path}`"
                                    class="rounded-lg border border-slate-200 bg-white p-3"
                                >
                                    <div class="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                                        {{ field.label || field.path }}
                                    </div>
                                    <div
                                        v-if="isContextRichText(field, readContextPath(request, field.path))"
                                        class="mt-2 text-sm leading-6 text-slate-800 [&_a]:font-medium [&_a]:text-blue-600 [&_a]:underline [&_em]:italic [&_li]:my-1 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-2 [&_strong]:font-semibold [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5"
                                        v-html="displayRichText(readContextPath(request, field.path))"
                                    />
                                    <div v-else class="mt-2 whitespace-pre-wrap text-sm text-slate-800">
                                        {{ displayValue(readContextPath(request, field.path)) }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="!isHumanInputRequest(request) && editableFields(request).length" class="mt-4 space-y-3">
                            <div class="text-sm font-semibold text-slate-900">{{ t('tamepulse.approvals.editableFields') }}</div>
                            <div
                                v-for="field in editableFields(request)"
                                :key="`${request.id}-editable-${field.path}`"
                            >
                                <Label>{{ field.label || field.path }}</Label>
                                <select
                                    v-if="field.type === 'boolean'"
                                    v-model="draftFor(request).editable_values[field.path]"
                                    class="cm-select-input"
                                >
                                    <option value="true">{{ t('tamepulse.common.yes') }}</option>
                                    <option value="false">{{ t('tamepulse.common.no') }}</option>
                                </select>
                                <Text
                                    v-else-if="field.type === 'number'"
                                    v-model="draftFor(request).editable_values[field.path]"
                                    type="number"
                                />
                                <RichTextEditor
                                    v-else-if="field.type === 'rich_text'"
                                    v-model="draftFor(request).editable_values[field.path]"
                                    :editable="true"
                                    height="180px"
                                    :placeholder="t('tamepulse.approvals.editSuggestedResponse')"
                                    enable-links
                                />
                                <TextArea
                                    v-else
                                    v-model="draftFor(request).editable_values[field.path]"
                                    rows="4"
                                />
                            </div>
                        </div>

                        <div class="mt-4 space-y-3 border-t border-slate-100 pt-4">
                            <template v-if="isHumanInputRequest(request)">
                                <div>
                                    <Label>{{ t('tamepulse.approvals.humanInput') }}</Label>
                                    <select
                                        v-if="humanInputType(request) === 'boolean'"
                                        v-model="draftFor(request).text"
                                        class="cm-select-input"
                                    >
                                        <option value="true">{{ t('tamepulse.common.yes') }}</option>
                                        <option value="false">{{ t('tamepulse.common.no') }}</option>
                                    </select>
                                    <Text
                                        v-else-if="humanInputType(request) === 'number'"
                                        v-model="draftFor(request).text"
                                        type="number"
                                    />
                                    <RichTextEditor
                                        v-else-if="humanInputType(request) === 'rich_text'"
                                        v-model="draftFor(request).text"
                                        :editable="true"
                                        height="180px"
                                        :placeholder="t('tamepulse.approvals.humanInputPlaceholder')"
                                        enable-links
                                    />
                                    <TextArea
                                        v-else
                                        v-model="draftFor(request).text"
                                        rows="4"
                                        :placeholder="t('tamepulse.approvals.humanInputPlaceholder')"
                                    />
                                </div>
                            </template>

                            <template v-else>
                                <div v-if="isDynamicReviewRequest(request)" class="space-y-3 rounded-xl border border-indigo-100 bg-indigo-50/40 p-3">
                                    <div>
                                        <div class="text-sm font-semibold text-slate-900">{{ t('tamepulse.approvals.dynamicReview') }}</div>
                                        <div class="text-xs text-slate-500">{{ t('tamepulse.approvals.dynamicReviewHelp') }}</div>
                                    </div>

                                    <div v-if="!dynamicReviewItems(request).length" class="rounded-lg border border-dashed border-indigo-200 bg-white p-3 text-sm text-slate-500">
                                        {{ t('tamepulse.approvals.noDynamicReviewItems') }}
                                    </div>

                                    <div v-else class="space-y-2">
                                        <div
                                            v-for="item in dynamicReviewItems(request)"
                                            :key="`${request.id}-review-item-${item.id}`"
                                            class="rounded-lg border border-white bg-white p-3 shadow-sm"
                                        >
                                            <label class="flex cursor-pointer items-start gap-3">
                                                <input
                                                    type="checkbox"
                                                    class="mt-1 h-4 w-4 rounded border-slate-300 accent-indigo-600"
                                                    :checked="isDynamicReviewItemSelected(request, item.id)"
                                                    @change="toggleDynamicReviewItem(request, item.id)"
                                                />
                                                <span class="min-w-0 flex-1">
                                                    <span class="block text-sm font-semibold text-slate-900">{{ item.title || item.id }}</span>
                                                    <span v-if="item.type" class="mt-1 inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">{{ item.type }}</span>
                                                    <span v-if="item.description" class="mt-2 block whitespace-pre-wrap text-sm text-slate-600">{{ item.description }}</span>
                                                    <span v-if="item.question" class="mt-2 block whitespace-pre-wrap text-sm font-medium text-slate-800">{{ item.question }}</span>
                                                </span>
                                            </label>

                                            <div v-if="dynamicReviewAllowsEditing(request)" class="mt-3 grid gap-2">
                                                <Text
                                                    :model-value="dynamicReviewItemField(request, item, 'title')"
                                                    :placeholder="t('tamepulse.approvals.itemTitle')"
                                                    @update:modelValue="setDynamicReviewItemField(request, item, 'title', $event)"
                                                />
                                                <TextArea
                                                    :model-value="dynamicReviewItemField(request, item, 'description')"
                                                    rows="3"
                                                    :placeholder="t('tamepulse.approvals.itemDescription')"
                                                    @update:modelValue="setDynamicReviewItemField(request, item, 'description', $event)"
                                                />
                                            </div>

                                            <TextArea
                                                v-if="dynamicReviewAllowsNotes(request)"
                                                class="mt-3"
                                                :model-value="dynamicReviewItemNote(request, item.id)"
                                                rows="2"
                                                :placeholder="t('tamepulse.approvals.itemNotePlaceholder')"
                                                @update:modelValue="setDynamicReviewItemNote(request, item.id, $event)"
                                            />
                                        </div>
                                    </div>

                                    <div v-if="dynamicReviewAllowsAddition(request)" class="rounded-lg border border-dashed border-indigo-200 bg-white p-3">
                                        <div class="mb-2 text-sm font-semibold text-slate-900">{{ t('tamepulse.approvals.addReviewItem') }}</div>
                                        <div class="grid gap-2">
                                            <Text v-model="draftFor(request).dynamic_review.manual_title" :placeholder="t('tamepulse.approvals.itemTitle')" />
                                            <TextArea v-model="draftFor(request).dynamic_review.manual_description" rows="3" :placeholder="t('tamepulse.approvals.itemDescription')" />
                                            <Button type="button" class="secondary" @click="addDynamicManualItem(request)">
                                                <TextWithIcon icon="plus">{{ t('tamepulse.common.add') }}</TextWithIcon>
                                            </Button>
                                        </div>

                                        <div v-if="draftFor(request).dynamic_review.manual_items.length" class="mt-3 space-y-2">
                                            <div
                                                v-for="item in draftFor(request).dynamic_review.manual_items"
                                                :key="`${request.id}-manual-review-${item.id}`"
                                                class="flex items-start justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 p-2 text-sm"
                                            >
                                                <div>
                                                    <div class="font-semibold text-slate-900">{{ item.title }}</div>
                                                    <div v-if="item.description" class="mt-1 whitespace-pre-wrap text-slate-600">{{ item.description }}</div>
                                                </div>
                                                <button type="button" class="text-xs font-semibold text-red-600" @click="removeDynamicManualItem(request, item.id)">
                                                    {{ t('tamepulse.common.delete') }}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <Label>{{ t('tamepulse.approvals.finalBrief') }}</Label>
                                        <TextArea v-model="draftFor(request).dynamic_review.final_brief" rows="3" :placeholder="t('tamepulse.approvals.finalBriefPlaceholder')" />
                                    </div>
                                </div>

                                <div>
                                    <Label>{{ t('tamepulse.approvals.decision') }}</Label>
                                    <div class="grid gap-2">
                                        <button
                                            v-for="decision in decisionOptions(request)"
                                            :key="`${request.id}-decision-${decision.key}`"
                                            type="button"
                                            class="rounded-lg border px-3 py-2 text-left text-sm font-semibold transition"
                                            :class="draftFor(request).decision_key === decision.key ? 'border-indigo-500 bg-indigo-50 text-indigo-800' : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-200'"
                                            @click="draftFor(request).decision_key = decision.key"
                                        >
                                            {{ decision.label }}
                                        </button>
                                    </div>
                                </div>
                            </template>

                            <div>
                                <Label>{{ t('tamepulse.approvals.note') }}</Label>
                                <TextArea v-model="draftFor(request).note" rows="3" />
                            </div>

                            <Button
                                type="button"
                                class="w-full"
                                :disabled="isResolving(request) || submitDisabled(request)"
                                @click="resolveRequest(request)"
                            >
                                <TextWithIcon :icon="isResolving(request) ? 'circle-notch' : 'check'">
                                    {{ isResolving(request) ? t('tamepulse.requestCenter.sending') : submitLabel(request) }}
                                </TextWithIcon>
                            </Button>
                        </div>
                        </div>
                    </article>
                </section>
            </template>
        </div>
    </SideDrawer>
</template>

<script setup>
import { computed, defineAsyncComponent, onBeforeUnmount, reactive, ref, watch } from 'vue'
import SideDrawer from '@/components/admin/SideDrawer.vue'
import { tenantHasModule } from '@/composables/useTenantModules.js'
import { useGlobals, useMutation, useQuery } from '@/composables/useGlobals'

const { gql, addFlashMessage, showError } = useGlobals()
const { t } = useI18n()
const RichTextEditor = defineAsyncComponent(() => import('@/components/admin/RichTextEditor.vue'))
const REQUEST_CENTER_MODULES = ['approvals', 'playbooks', 'agents']

const props = defineProps({
    launcherMode: {
        type: String,
        default: 'floating',
        validator: (value) => ['floating', 'inline'].includes(value),
    },
})

const drawerOpen = ref(false)
const dismissedIds = ref(new Set())
const dismissedNoticeIds = ref(new Set())
const resolvingIds = ref(new Set())
const markingNoticeReadIds = ref(new Set())
const approvalChannelName = ref(null)
const noticeChannelName = ref(null)
const drafts = reactive({})
const openItemId = ref('')

const { result: tenantResult } = useQuery(gql`
    query TamepulseRequestCenterTenant {
        currentTenant {
            id
            type
            active_modules
        }
    }
`)

const currentTenant = computed(() => tenantResult.value?.currentTenant ?? null)
const currentTenantId = computed(() => currentTenant.value?.id)
const requestCenterEnabled = computed(() => {
    return currentTenant.value?.type === 'tamepulse'
        && REQUEST_CENTER_MODULES.some((moduleKey) => tenantHasModule(currentTenant.value, moduleKey))
})

const {
    result: requestsResult,
    loading: requestsLoading,
    refetch: refetchRequests,
} = useQuery(gql`
    query TamepulseRequestCenterRequests($first: Int!, $page: Int, $status: String, $dashboardOnly: Boolean, $mine: Boolean) {
        tamepulseApprovalRequestPaginated(
            perPage: $first
            page: $page
            status: $status
            dashboardOnly: $dashboardOnly
            mine: $mine
        ) {
            paginatorInfo {
                total
            }
            data {
                id
                playbook_id
                run_id
                node_id
                node_label
                status
                channel
                message
                decision_options
                context_fields
                editable_fields
                runtime_state
                context_snapshot
                edited_values
                resolved_context
                decision_key
                decision_label
                note
                requested_at
                playbook { id name }
                step { id node_type }
                run {
                    id
                    status
                    environment { id name slug }
                }
            }
        }
    }
`, {
    first: 50,
    page: 1,
    status: 'pending',
    dashboardOnly: true,
    mine: true,
}, {
    enabled: requestCenterEnabled,
    fetchPolicy: 'cache-and-network',
})

const { mutate: resolveApprovalMutation } = useMutation(gql`
    mutation TamepulseRequestCenterResolve($id: ID!, $input: TamepulseApprovalResolveInput!) {
        tamepulseApprovalResolve(id: $id, input: $input) {
            id
            status
            decision_key
            decision_label
            edited_values
            resolved_context
            note
            decided_at
        }
    }
`)

const {
    result: noticesResult,
    loading: noticesLoading,
    refetch: refetchNotices,
} = useQuery(gql`
    query NoticePaginated($first: Int!, $page: Int, $status: String, $mine: Boolean) {
        noticePaginated(
            perPage: $first
            page: $page
            status: $status
            mine: $mine
        ) {
            paginatorInfo {
                total
            }
            data {
                id
                site_id
                type
                playbook_id
                run_id
                run_step_id
                node_id
                node_label
                title
                message
                level
                format
                payload
                target_type
                target_user_id
                status
                created_at
                playbook { id name }
                step { id node_type }
                run {
                    id
                    status
                    environment { id name slug }
                }
            }
        }
    }
`, {
    first: 50,
    page: 1,
    status: 'unread',
    mine: true,
}, {
    enabled: requestCenterEnabled,
    fetchPolicy: 'cache-and-network',
})

const { mutate: markNoticeReadMutation } = useMutation(gql`
    mutation NoticeMarkRead($id: ID!) {
        noticeMarkRead(id: $id) {
            id
            status
            read_at
            dismissed_at
        }
    }
`)

const rawRequests = computed(() => requestsResult.value?.tamepulseApprovalRequestPaginated?.data ?? [])
const rawPendingTotal = computed(() => Number(requestsResult.value?.tamepulseApprovalRequestPaginated?.paginatorInfo?.total ?? 0))
const rawNotices = computed(() => noticesResult.value?.noticePaginated?.data ?? [])
const rawUnreadNoticesTotal = computed(() => Number(noticesResult.value?.noticePaginated?.paginatorInfo?.total ?? 0))
const pendingRequests = computed(() => {
    return rawRequests.value.filter((request) => {
        return request.status === 'pending'
            && request.channel === 'admin'
            && request.run?.status === 'waiting'
            && !dismissedIds.value.has(String(request.id))
    })
})
const pendingNotices = computed(() => {
    return rawNotices.value.filter((notice) => {
        return notice.status === 'unread'
            && !dismissedNoticeIds.value.has(String(notice.id))
    })
})
const pendingRequestCount = computed(() => Math.max(0, rawPendingTotal.value - dismissedIds.value.size))
const pendingNoticeCount = computed(() => Math.max(0, rawUnreadNoticesTotal.value - dismissedNoticeIds.value.size))
const pendingCount = computed(() => pendingRequestCount.value + pendingNoticeCount.value)
const displayPendingCount = computed(() => pendingCount.value > 99 ? '99+' : String(pendingCount.value))
const pendingItemIds = computed(() => [
    ...pendingNotices.value.map((notice) => itemKey('notice', notice)),
    ...pendingRequests.value.map((request) => itemKey('request', request)),
])
const inlineLauncher = computed(() => props.launcherMode === 'inline')
const showLauncher = computed(() => {
    if (!requestCenterEnabled.value) {
        return false
    }

    return inlineLauncher.value || pendingCount.value > 0 || drawerOpen.value
})
const centerLoading = computed(() => requestsLoading.value || noticesLoading.value)
const hasPendingItems = computed(() => pendingRequests.value.length > 0 || pendingNotices.value.length > 0)
const groupedNotices = computed(() => {
    const groups = new Map()

    pendingNotices.value.forEach((notice) => {
        const group = noticeGroup(notice)
        const key = group.key
        if (!groups.has(key)) {
            groups.set(key, {
                key,
                name: group.name,
                label: group.label,
                notices: [],
            })
        }

        groups.get(key).notices.push(notice)
    })

    return Array.from(groups.values())
})
const groupedRequests = computed(() => {
    const groups = new Map()

    pendingRequests.value.forEach((request) => {
        const key = String(request.playbook_id || 'unknown')
        if (!groups.has(key)) {
            groups.set(key, {
                key,
                name: request.playbook?.name || t('tamepulse.requestCenter.unknownPlaybook'),
                requests: [],
            })
        }

        groups.get(key).requests.push(request)
    })

    return Array.from(groups.values())
})

watch(pendingRequests, (requests) => {
    const activeIds = new Set(requests.map((request) => String(request.id)))

    requests.forEach((request) => {
        const id = String(request.id)
        if (!drafts[id]) {
            drafts[id] = createDraft(request)
        }
    })

    Object.keys(drafts).forEach((id) => {
        if (!activeIds.has(id)) {
            delete drafts[id]
        }
    })
}, { immediate: true })

watch(pendingItemIds, (itemIds) => {
    if (openItemId.value && !itemIds.includes(openItemId.value)) {
        openItemId.value = ''
    }
}, { immediate: true })

watch(requestsResult, () => {
    const rawIds = new Set(rawRequests.value.map((request) => String(request.id)))
    dismissedIds.value = new Set([...dismissedIds.value].filter((id) => rawIds.has(id)))
})

watch(noticesResult, () => {
    const rawIds = new Set(rawNotices.value.map((notice) => String(notice.id)))
    dismissedNoticeIds.value = new Set([...dismissedNoticeIds.value].filter((id) => rawIds.has(id)))
})

watch(drawerOpen, (open) => {
    if (open && requestCenterEnabled.value) {
        refetchRequests?.()
        refetchNotices?.()
    }
})

watch([requestCenterEnabled, currentTenantId], () => {
    subscribeToApprovalUpdates()
    subscribeToNoticeUpdates()
}, { immediate: true })

onBeforeUnmount(() => {
    leaveApprovalUpdatesChannel()
    leaveNoticeUpdatesChannel()
})

function itemKey(type, item) {
    return `${type}:${item.id}`
}

function itemPanelId(type, item) {
    return `request-center-${type}-${item.id}`
}

function isItemOpen(type, item) {
    return openItemId.value === itemKey(type, item)
}

function toggleItem(type, item) {
    const itemId = itemKey(type, item)

    openItemId.value = openItemId.value === itemId ? '' : itemId
}

function createDraft(request) {
    const editedValues = normalizeJsonObject(request?.edited_values)

    return {
        decision_key: isHumanInputRequest(request) ? '' : (request.decision_key || decisionOptions(request)[0]?.key || ''),
        note: request.note || '',
        text: isHumanInputRequest(request)
            ? String(editedValues.text ?? humanInputOutput(request)?.text ?? (humanInputType(request) === 'boolean' ? 'true' : ''))
            : '',
        editable_values: Object.fromEntries(
            editableFields(request).map((field) => [
                field.path,
                formatEditableValue(readEditableFieldValue(request, field), field.type),
            ])
        ),
        dynamic_review: createDynamicReviewDraft(request, editedValues.dynamic_review),
    }
}

function draftFor(request) {
    const id = String(request.id)

    if (!drafts[id]) {
        drafts[id] = createDraft(request)
    }

    return drafts[id]
}

async function resolveRequest(request) {
    if (isResolving(request) || submitDisabled(request)) {
        return
    }

    setResolving(request, true)

    try {
        const draft = draftFor(request)
        const input = isHumanInputRequest(request)
            ? {
                text: draft.text,
                note: draft.note,
            }
            : {
                decision_key: draft.decision_key,
                note: draft.note,
                edited_values: normalizeEditedValues(request),
            }

        await resolveApprovalMutation({
            id: request.id,
            input,
        })

        dismissRequest(request)
        addFlashMessage({
            type: ['cm-alert-success'],
            message: isHumanInputRequest(request) ? t('tamepulse.approvals.inputSubmitted') : t('tamepulse.approvals.decisionApplied'),
        })
        await refetchRequests?.()
    } catch (error) {
        showError(error)
    } finally {
        setResolving(request, false)
    }
}

async function markNoticeRead(notice) {
    if (isMarkingNoticeRead(notice)) {
        return
    }

    setMarkingNoticeRead(notice, true)

    try {
        await markNoticeReadMutation({
            id: notice.id,
        })

        dismissNotice(notice)
        addFlashMessage({
            type: ['cm-alert-success'],
            message: t('tamepulse.requestCenter.noticeRead'),
        })
        await refetchNotices?.()
    } catch (error) {
        showError(error)
    } finally {
        setMarkingNoticeRead(notice, false)
    }
}

function dismissRequest(request) {
    dismissedIds.value = new Set([...dismissedIds.value, String(request.id)])
}

function dismissNotice(notice) {
    dismissedNoticeIds.value = new Set([...dismissedNoticeIds.value, String(notice.id)])
}

function normalizeEditedValues(request) {
    const draft = draftFor(request)
    const values = Object.fromEntries(editableFields(request).map((field) => {
        const value = draft.editable_values[field.path]

        if (field.type === 'number') {
            return [field.path, value === '' || value === null || value === undefined ? null : Number(value)]
        }

        if (field.type === 'boolean') {
            return [field.path, value === true || value === 'true']
        }

        return [field.path, value ?? '']
    }))

    if (isDynamicReviewRequest(request)) {
        values.dynamic_review = normalizeDynamicReviewDraft(request)
    }

    return values
}

function submitDisabled(request) {
    if (isHumanInputRequest(request)) {
        return humanInputRequired(request) && String(draftFor(request).text ?? '').trim() === ''
    }

    if (isDynamicReviewRequest(request) && dynamicReviewRequiresSelection(request)) {
        const draft = draftFor(request).dynamic_review

        if (!draft.selected_item_ids.length && !draft.manual_items.length) {
            return true
        }
    }

    return !draftFor(request).decision_key
}

function submitLabel(request) {
    return isHumanInputRequest(request)
        ? t('tamepulse.approvals.submitInput')
        : t('tamepulse.approvals.applyDecision')
}

function requestTypeLabel(request) {
    return isHumanInputRequest(request)
        ? t('tamepulse.requestCenter.types.humanInput')
        : t('tamepulse.requestCenter.types.approval')
}

function normalizeJsonValue(value) {
    if (typeof value !== 'string') {
        return value
    }

    const trimmed = value.trim()
    if (!trimmed) {
        return null
    }

    try {
        return JSON.parse(trimmed)
    } catch {
        return value
    }
}

function normalizeJsonObject(value) {
    const normalized = normalizeJsonValue(value)

    return normalized && typeof normalized === 'object' && !Array.isArray(normalized) ? normalized : {}
}

function normalizeJsonArray(value) {
    const normalized = normalizeJsonValue(value)

    return Array.isArray(normalized) ? normalized : []
}

function runtimeState(request) {
    return normalizeJsonObject(request?.runtime_state)
}

function isHumanInputRequest(request) {
    if (runtimeState(request).request_type === 'human_input') {
        return true
    }

    if (request?.step?.node_type === 'human_input') {
        return true
    }

    return normalizeJsonArray(request?.decision_options).length === 0
        && normalizeJsonArray(request?.editable_fields).length === 0
}

function humanInputType(request) {
    const type = runtimeState(request).input_type

    return ['text', 'textarea', 'rich_text', 'number', 'boolean'].includes(type) ? type : 'textarea'
}

function humanInputRequired(request) {
    return runtimeState(request).required !== false
}

function humanInputOutput(request) {
    const outputKey = runtimeState(request).output_key
    const resolvedContext = normalizeJsonObject(request?.resolved_context)
    if (!outputKey || !Object.keys(resolvedContext).length) return null

    const value = resolvedContext[outputKey]

    return value && typeof value === 'object' ? value : null
}

function contextFields(request) {
    return normalizeJsonArray(request?.context_fields)
}

function editableFields(request) {
    return normalizeJsonArray(request?.editable_fields)
}

function decisionOptions(request) {
    const options = normalizeJsonArray(request?.decision_options)

    if (isHumanInputRequest(request)) {
        return []
    }

    return options.length ? options : [
        { key: 'approve', label: t('tamepulse.approvals.approve') },
        { key: 'reject', label: t('tamepulse.approvals.reject') },
    ]
}

function createDynamicReviewDraft(request, previousValue = null) {
    const previous = previousValue && typeof previousValue === 'object' && !Array.isArray(previousValue) ? previousValue : {}
    const items = dynamicReviewItems(request)
    const defaultSelectedIds = items
        .filter((item) => item.default_selected)
        .map((item) => item.id)

    return {
        selected_item_ids: normalizeStringList(previous.selected_item_ids).length
            ? normalizeStringList(previous.selected_item_ids)
            : defaultSelectedIds,
        notes_by_item: normalizeJsonObject(previous.notes_by_item),
        edited_items: normalizeJsonObject(previous.edited_items),
        manual_items: normalizeJsonArray(previous.manual_items),
        manual_title: '',
        manual_description: '',
        final_brief: String(previous.final_brief ?? ''),
    }
}

function dynamicReviewState(request) {
    const review = runtimeState(request).dynamic_review

    return review && typeof review === 'object' && !Array.isArray(review) ? review : {}
}

function isDynamicReviewRequest(request) {
    return runtimeState(request).approval_mode === 'dynamic_checklist'
        && Object.keys(dynamicReviewState(request)).length > 0
}

function dynamicReviewItems(request) {
    return normalizeJsonArray(dynamicReviewState(request).items)
        .map((item, index) => {
            if (!item || typeof item !== 'object') {
                return {
                id: `item_${index + 1}`,
                title: String(item ?? t('tamepulse.approvals.reviewItemFallback', { number: index + 1 })),
                    description: '',
                    type: '',
                    question: '',
                    default_selected: false,
                }
            }

            const id = String(item.id || `item_${index + 1}`)

            return {
                ...item,
                id,
                title: String(item.title || id),
                description: item.description ? String(item.description) : '',
                type: item.type ? String(item.type) : '',
                question: item.question ? String(item.question) : '',
                default_selected: item.default_selected === true,
            }
        })
}

function dynamicReviewAllowsNotes(request) {
    return dynamicReviewState(request).allow_item_notes !== false
}

function dynamicReviewAllowsEditing(request) {
    return dynamicReviewState(request).allow_item_editing !== false
}

function dynamicReviewAllowsAddition(request) {
    return dynamicReviewState(request).allow_item_addition !== false
}

function dynamicReviewRequiresSelection(request) {
    return dynamicReviewState(request).require_selection === true
}

function isDynamicReviewItemSelected(request, itemId) {
    return draftFor(request).dynamic_review.selected_item_ids.includes(String(itemId))
}

function toggleDynamicReviewItem(request, itemId) {
    const draft = draftFor(request).dynamic_review
    const id = String(itemId)

    draft.selected_item_ids = draft.selected_item_ids.includes(id)
        ? draft.selected_item_ids.filter((selectedId) => selectedId !== id)
        : [...draft.selected_item_ids, id]
}

function dynamicReviewItemField(request, item, field) {
    const edits = draftFor(request).dynamic_review.edited_items[item.id] ?? {}

    return edits[field] ?? item[field] ?? ''
}

function setDynamicReviewItemField(request, item, field, value) {
    const draft = draftFor(request).dynamic_review

    draft.edited_items[item.id] = {
        ...(draft.edited_items[item.id] ?? {}),
        [field]: value,
    }
}

function dynamicReviewItemNote(request, itemId) {
    return draftFor(request).dynamic_review.notes_by_item[String(itemId)] ?? ''
}

function setDynamicReviewItemNote(request, itemId, value) {
    draftFor(request).dynamic_review.notes_by_item[String(itemId)] = value
}

function addDynamicManualItem(request) {
    const draft = draftFor(request).dynamic_review
    const title = String(draft.manual_title || '').trim()
    const description = String(draft.manual_description || '').trim()

    if (!title && !description) {
        return
    }

    draft.manual_items.push({
        id: `manual_${Date.now()}_${draft.manual_items.length + 1}`,
        title: title || t('tamepulse.approvals.manualItem'),
        description,
    })
    draft.manual_title = ''
    draft.manual_description = ''
}

function removeDynamicManualItem(request, itemId) {
    const draft = draftFor(request).dynamic_review

    draft.manual_items = draft.manual_items.filter((item) => item.id !== itemId)
}

function normalizeDynamicReviewDraft(request) {
    const draft = draftFor(request).dynamic_review

    return {
        selected_item_ids: normalizeStringList(draft.selected_item_ids),
        notes_by_item: Object.fromEntries(Object.entries(draft.notes_by_item ?? {})
            .map(([id, value]) => [String(id), String(value ?? '').trim()])
            .filter(([id, value]) => id && value)),
        edited_items: normalizeJsonObject(draft.edited_items),
        manual_items: normalizeJsonArray(draft.manual_items)
            .map((item) => ({
                id: String(item?.id || ''),
                title: String(item?.title || '').trim(),
                description: String(item?.description || '').trim(),
            }))
            .filter((item) => item.title || item.description),
        final_brief: String(draft.final_brief ?? '').trim(),
    }
}

function normalizeStringList(value) {
    return Array.from(new Set((Array.isArray(value) ? value : [])
        .map((item) => String(item ?? '').trim())
        .filter(Boolean)))
}

function readContextPath(request, path, source = normalizeJsonObject(request?.context_snapshot)) {
    const result = readContextPathResult(path, source)

    return result.found ? result.value : null
}

function readContextPathResult(path, source) {
    const segments = String(path || '').split('.').filter(Boolean)
    let current = normalizeJsonValue(source)

    for (const segment of segments) {
        if (current && typeof current === 'object' && Object.prototype.hasOwnProperty.call(current, segment)) {
            current = current[segment]
            continue
        }

        return { found: false, value: null }
    }

    return { found: true, value: current }
}

function readEditableFieldValue(request, field) {
    const resolvedValue = readContextPathResult(field.path, normalizeJsonObject(request?.resolved_context))
    if (resolvedValue.found) return resolvedValue.value

    const editedValues = normalizeJsonObject(request?.edited_values)
    if (
        Object.keys(editedValues).length
        && Object.prototype.hasOwnProperty.call(editedValues, field.path)
    ) {
        return editedValues[field.path]
    }

    return readContextPath(request, field.path)
}

function displayValue(value) {
    if (value === null || value === undefined || value === '') return '-'
    if (typeof value === 'object') return JSON.stringify(value, null, 2)

    return String(value)
}

const richTextHtmlPattern = /<\/?(p|br|strong|em|ul|ol|li|a)\b/i
const blockedRichTextHtmlPattern = /<\/?(script|style|iframe|object|embed|html|head|body)\b/i
const allowedRichTextTags = new Set(['p', 'br', 'strong', 'em', 'ul', 'ol', 'li', 'a'])
const removedRichTextTags = new Set(['script', 'style', 'iframe', 'object', 'embed', 'html', 'head', 'body'])

function isContextRichText(field, value) {
    if (field?.type === 'rich_text') return true

    return looksLikeRichTextHtml(value)
}

function looksLikeRichTextHtml(value) {
    if (typeof value !== 'string') return false

    return richTextHtmlPattern.test(value) && !blockedRichTextHtmlPattern.test(value)
}

function displayRichText(value) {
    if (value === null || value === undefined || value === '') return '-'

    return sanitizeRichTextHtml(typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)) || '-'
}

function sanitizeRichTextHtml(value) {
    if (typeof document === 'undefined') {
        return escapeHtml(value)
    }

    const template = document.createElement('template')
    template.innerHTML = value
    sanitizeRichTextChildren(template.content)

    return template.innerHTML.trim()
}

function sanitizeRichTextChildren(parent) {
    Array.from(parent.childNodes).forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            return
        }

        if (node.nodeType !== Node.ELEMENT_NODE) {
            node.remove()
            return
        }

        const tag = node.tagName.toLowerCase()

        if (removedRichTextTags.has(tag)) {
            node.remove()
            return
        }

        sanitizeRichTextChildren(node)

        if (!allowedRichTextTags.has(tag)) {
            node.replaceWith(...Array.from(node.childNodes))
            return
        }

        const href = tag === 'a' ? node.getAttribute('href') : null
        Array.from(node.attributes).forEach((attribute) => node.removeAttribute(attribute.name))

        if (tag === 'a' && isSafeRichTextUrl(href)) {
            node.setAttribute('href', href.trim())
            node.setAttribute('rel', 'noopener noreferrer')
        }
    })
}

function isSafeRichTextUrl(value) {
    const href = String(value || '').trim()

    if (!href || href.startsWith('//')) return false
    if (href.startsWith('#') || href.startsWith('/')) return true
    if (!/^[a-z][a-z0-9+.-]*:/i.test(href)) return false

    try {
        return ['http:', 'https:', 'mailto:', 'tel:'].includes(new URL(href).protocol)
    } catch {
        return false
    }
}

function escapeHtml(value) {
    return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
}

function formatEditableValue(value, type) {
    if (value === null || value === undefined) return ''
    if (type === 'boolean') return value ? 'true' : 'false'

    return typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)
}

function isResolving(request) {
    return resolvingIds.value.has(String(request.id))
}

function setResolving(request, resolving) {
    const ids = new Set(resolvingIds.value)

    if (resolving) {
        ids.add(String(request.id))
    } else {
        ids.delete(String(request.id))
    }

    resolvingIds.value = ids
}

function isMarkingNoticeRead(notice) {
    return markingNoticeReadIds.value.has(String(notice.id))
}

function setMarkingNoticeRead(notice, resolving) {
    const ids = new Set(markingNoticeReadIds.value)

    if (resolving) {
        ids.add(String(notice.id))
    } else {
        ids.delete(String(notice.id))
    }

    markingNoticeReadIds.value = ids
}

function subscribeToApprovalUpdates() {
    leaveApprovalUpdatesChannel()

    if (!window.Echo || !requestCenterEnabled.value || !currentTenantId.value) {
        return
    }

    const channelName = `tamepulse.approvals.${currentTenantId.value}`
    approvalChannelName.value = channelName

    window.Echo.channel(channelName)
        .listen('.TamepulseApprovalRequestsUpdated', () => {
            refetchRequests?.()
        })
}

function subscribeToNoticeUpdates() {
    leaveNoticeUpdatesChannel()

    if (!window.Echo || !requestCenterEnabled.value || !currentTenantId.value) {
        return
    }

    const channelName = `notices.${currentTenantId.value}`
    noticeChannelName.value = channelName

    window.Echo.channel(channelName)
        .listen('.NoticesUpdated', () => {
            refetchNotices?.()
        })
}

function leaveApprovalUpdatesChannel() {
    if (window.Echo && approvalChannelName.value) {
        window.Echo.leave(approvalChannelName.value)
    }

    approvalChannelName.value = null
}

function leaveNoticeUpdatesChannel() {
    if (window.Echo && noticeChannelName.value) {
        window.Echo.leave(noticeChannelName.value)
    }

    noticeChannelName.value = null
}

function compactSummary(value) {
    return String(value ?? '').replace(/\s+/g, ' ').trim()
}

function requestSummary(request) {
    const message = compactSummary(request?.message)
    if (message) return message

    const field = contextFields(request)[0]
    if (!field) return ''

    return compactSummary(displayValue(readContextPath(request, field.path)))
}

function noticeSummary(notice) {
    return compactSummary(noticeMessage(notice))
}

function noticePayload(notice) {
    return notice?.payload && typeof notice.payload === 'object' && !Array.isArray(notice.payload)
        ? notice.payload
        : {}
}

function isHumanReviewNotice(notice) {
    return notice?.type === 'human_review'
}

function noticeGroup(notice) {
    if (isHumanReviewNotice(notice)) {
        const payload = noticePayload(notice)
        const procedureId = String(payload.procedure_id || 'unknown')

        return {
            key: `procedure:${procedureId}`,
            label: t('tamepulse.requestCenter.procedureGroup'),
            name: String(payload.procedure_name || t('tamepulse.requestCenter.unknownProcedure')),
        }
    }

    return {
        key: `playbook:${String(notice?.playbook_id || 'unknown')}`,
        label: t('tamepulse.requestCenter.playbookGroup'),
        name: notice?.playbook?.name || t('tamepulse.requestCenter.unknownPlaybook'),
    }
}

function noticeTypeLabel(notice) {
    return isHumanReviewNotice(notice)
        ? t('tamepulse.requestCenter.types.humanReview')
        : t('tamepulse.requestCenter.types.notice')
}

function humanReviewVisitorMessage(notice) {
    if (!isHumanReviewNotice(notice)) return ''

    return String(noticePayload(notice).visitor_message || '').trim()
}

function noticeMessage(notice) {
    if (notice?.format !== 'json') {
        return notice?.message || '-'
    }

    try {
        return JSON.stringify(JSON.parse(notice.message), null, 2)
    } catch {
        return notice?.message || '-'
    }
}

function formatDateTime(value) {
    if (!value) return '-'

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '-'

    return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'short',
        timeStyle: 'short',
    }).format(date)
}
</script>
